/**
 * Comprehensive Clinic Management Dashboard
 * Provides access to all clinic management features
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  Message as MessageIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  DateRange as DateIcon,
  LocalHospital as HospitalIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  FileUpload as UploadFileIcon,
  FileDownload as DownloadFileIcon,
  FolderOpen as FolderOpenIcon,
  CreateNewFolder as CreateFolderIcon,
  DriveFileMove as MoveIcon,
  DriveFileRenameOutline as RenameIcon,
  ContentCopy as CopyIcon,
  Archive as ArchiveIcon,
  RestoreFromTrash as RestoreIcon,
  DeleteForever as DeleteForeverIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Share as ShareIcon,
  Notifications as NotificationIcon,
  Description as DocumentIcon,
  Computer as ComputerIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Psychology as PsychologyIcon,
  LocalPharmacy as PharmacyIcon,
  Favorite as HeartIcon,
  Sports as SportsIcon,
  Restaurant as FoodIcon,
  SmokingRooms as SmokingIcon,
  LocalDrink as DrinkIcon,
  Bedtime as SleepIcon,
  FitnessCenter as FitnessIcon,
  Gavel as GavelIcon,
  Policy as PolicyIcon,
  Shield as ShieldIcon,
  VerifiedUser as VerifiedUserIcon,
  DataUsage as DataUsageIcon,
  Storage as StorageIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Event as EventIcon,
  MonetizationOn as MonetizationOnIcon,
  Chat as ChatIcon,
  Dashboard as DashboardIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// API Services
import {
  clinicProfileApi,
  clinicStaffApi,
  clinicAppointmentApi,
  clinicBillingApi,
  clinicCommunicationApi,
  clinicAnalyticsApi,
  clinicSettingsApi
} from '../../services/api/clinicManagement';

const ClinicManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Data states
  const [profileData, setProfileData] = useState(null);
  const [staffData, setStaffData] = useState([]);
  const [appointmentsData, setAppointmentsData] = useState([]);
  const [billingData, setBillingData] = useState([]);
  const [communicationsData, setCommunicationsData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [settingsData, setSettingsData] = useState(null);
  
  // Dialog states
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showStaffDialog, setShowStaffDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showAnalyticsDialog, setShowAnalyticsDialog] = useState(false);

  // Form states
  const [profileForm, setProfileForm] = useState({
    clinic_name: '',
    phone_number: '',
    address: '',
    email: '',
    city: '',
    specialization: '',
    working_hours: '',
    services_offered: '',
    latitude: '',
    longitude: '',
    subdomain: '',
    subscription_plan: 'basic'
  });

  const [staffForm, setStaffForm] = useState({
    user: '',
    role: 'receptionist',
    is_active: true
  });

  const [settingsForm, setSettingsForm] = useState({
    clinic_name: '',
    phone_number: '',
    address: '',
    email: '',
    city: '',
    specialization: '',
    working_hours: '',
    services_offered: '',
    latitude: '',
    longitude: '',
    subdomain: '',
    subscription_plan: 'basic'
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all clinic data in parallel
      const [
        profileResponse,
        staffResponse,
        appointmentsResponse,
        billingResponse,
        communicationsResponse,
        analyticsResponse,
        settingsResponse
      ] = await Promise.allSettled([
        clinicProfileApi.getProfile(),
        clinicStaffApi.getStaff(),
        clinicAppointmentApi.getAppointments(),
        clinicBillingApi.getBilling(),
        clinicCommunicationApi.getCommunications(),
        clinicAnalyticsApi.getAnalytics(),
        clinicSettingsApi.getSettings()
      ]);

      // Set data from successful responses
      if (profileResponse.status === 'fulfilled') {
        setProfileData(profileResponse.value);
        setProfileForm(profileResponse.value);
      }
      if (staffResponse.status === 'fulfilled') {
        setStaffData(staffResponse.value);
      }
      if (appointmentsResponse.status === 'fulfilled') {
        setAppointmentsData(appointmentsResponse.value);
      }
      if (billingResponse.status === 'fulfilled') {
        setBillingData(billingResponse.value);
      }
      if (communicationsResponse.status === 'fulfilled') {
        setCommunicationsData(communicationsResponse.value);
      }
      if (analyticsResponse.status === 'fulfilled') {
        setAnalyticsData(analyticsResponse.value);
      }
      if (settingsResponse.status === 'fulfilled') {
        setSettingsData(settingsResponse.value);
        setSettingsForm(settingsResponse.value);
      }

    } catch (err) {
      console.error('Error loading clinic data:', err);
      setError('Failed to load clinic data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    try {
      setLoading(true);
      await clinicProfileApi.updateProfile(profileForm);
      toast.success('Clinic profile updated successfully!');
      setShowProfileDialog(false);
      await loadAllData();
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStaffAdd = async () => {
    try {
      setLoading(true);
      await clinicStaffApi.addStaff(staffForm);
      toast.success('Staff member added successfully!');
      setShowStaffDialog(false);
      await loadAllData();
    } catch (err) {
      console.error('Error adding staff:', err);
      toast.error('Failed to add staff member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsUpdate = async () => {
    try {
      setLoading(true);
      await clinicSettingsApi.updateSettings(settingsForm);
      toast.success('Clinic settings updated successfully!');
      setShowSettingsDialog(false);
      await loadAllData();
    } catch (err) {
      console.error('Error updating settings:', err);
      toast.error('Failed to update settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCompletionPercentage = () => {
    if (!profileData) return 0;
    
    const requiredFields = [
      'clinic_name', 'phone_number', 'address', 'email', 'city',
      'specialization', 'working_hours', 'services_offered'
    ];
    
    const completedFields = requiredFields.filter(field => profileData[field]).length;
    return Math.round((completedFields / requiredFields.length) * 100);
  };

  const StatCard = ({ title, value, icon, color, onClick, loading, trend, trendValue }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s ease',
        '&:hover': onClick ? { transform: 'translateY(-4px)' } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h6" component="div">
              {loading ? <CircularProgress size={20} /> : value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Box>
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {trend === 'up' ? (
              <TrendingUpIcon sx={{ color: 'success.main', fontSize: 16 }} />
            ) : (
              <TrendingDownIcon sx={{ color: 'error.main', fontSize: 16 }} />
            )}
            <Typography variant="caption" color={trend === 'up' ? 'success.main' : 'error.main'}>
              {trendValue}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const FeatureCard = ({ title, description, icon, color, onClick, status, count }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
          {count !== undefined && (
            <Badge badgeContent={count} color="primary">
              <Chip 
                label={status} 
                size="small" 
                color={status === 'Active' ? 'success' : 'default'}
              />
            </Badge>
          )}
        </Box>
      </CardContent>
      <CardActions>
        <Button size="small" onClick={onClick}>
          Manage
        </Button>
      </CardActions>
    </Card>
  );

  if (loading && !profileData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Clinic Management Dashboard
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Profile Completion Status */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Profile Completion</Typography>
          <Chip 
            label={`${getCompletionPercentage()}% Complete`} 
            color={getCompletionPercentage() === 100 ? 'success' : 'primary'}
            variant="outlined"
          />
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={getCompletionPercentage()} 
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Paper>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Staff"
            value={analyticsData?.total_staff || 0}
            icon={<PeopleIcon />}
            color="primary.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Doctors"
            value={analyticsData?.total_doctors || 0}
            icon={<PersonIcon />}
            color="secondary.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Appointments"
            value={analyticsData?.total_appointments || 0}
            icon={<ScheduleIcon />}
            color="success.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Monthly Revenue"
            value={`$${analyticsData?.monthly_revenue || 0}`}
            icon={<MonetizationOnIcon />}
            color="info.main"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Feature Management */}
      <Grid container spacing={3}>
        {/* Profile Management */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Profile Management"
            description="Manage your clinic profile and information"
            icon={<BusinessIcon />}
            color="primary.main"
            onClick={() => setShowProfileDialog(true)}
            status={getCompletionPercentage() === 100 ? 'Complete' : 'Incomplete'}
          />
        </Grid>

        {/* Staff Management */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Staff Management"
            description="Manage clinic staff and their roles"
            icon={<PeopleIcon />}
            color="secondary.main"
            onClick={() => setShowStaffDialog(true)}
            status={staffData.length > 0 ? 'Active' : 'Empty'}
            count={staffData.length}
          />
        </Grid>

        {/* Appointment Management */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Appointment Management"
            description="View and manage clinic appointments"
            icon={<ScheduleIcon />}
            color="info.main"
            onClick={() => navigate('/clinic-dashboard/appointments')}
            status={appointmentsData.length > 0 ? 'Active' : 'Empty'}
            count={appointmentsData.length}
          />
        </Grid>

        {/* Billing Management */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Billing Management"
            description="View billing information and financial reports"
            icon={<ReceiptIcon />}
            color="success.main"
            onClick={() => navigate('/clinic-dashboard/billing')}
            status={billingData.length > 0 ? 'Active' : 'Empty'}
            count={billingData.length}
          />
        </Grid>

        {/* Communication System */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Communication System"
            description="Manage clinic communications and messaging"
            icon={<MessageIcon />}
            color="warning.main"
            onClick={() => navigate('/clinic-dashboard/communications')}
            status="Available"
          />
        </Grid>

        {/* Analytics Dashboard */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Analytics Dashboard"
            description="View performance metrics and analytics"
            icon={<AnalyticsIcon />}
            color="primary.main"
            onClick={() => setShowAnalyticsDialog(true)}
            status="Available"
          />
        </Grid>

        {/* Settings Management */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Settings Management"
            description="Configure clinic settings and preferences"
            icon={<SettingsIcon />}
            color="secondary.main"
            onClick={() => setShowSettingsDialog(true)}
            status="Available"
          />
        </Grid>
      </Grid>

      {/* Profile Update Dialog */}
      <Dialog open={showProfileDialog} onClose={() => setShowProfileDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Update Clinic Profile</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Clinic Name"
                value={profileForm.clinic_name}
                onChange={(e) => setProfileForm({ ...profileForm, clinic_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={profileForm.phone_number}
                onChange={(e) => setProfileForm({ ...profileForm, phone_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={profileForm.address}
                onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={profileForm.email}
                onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={profileForm.city}
                onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Specialization"
                value={profileForm.specialization}
                onChange={(e) => setProfileForm({ ...profileForm, specialization: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Working Hours"
                value={profileForm.working_hours}
                onChange={(e) => setProfileForm({ ...profileForm, working_hours: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Services Offered"
                value={profileForm.services_offered}
                onChange={(e) => setProfileForm({ ...profileForm, services_offered: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowProfileDialog(false)}>Cancel</Button>
          <Button onClick={handleProfileUpdate} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Update Profile'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Staff Dialog */}
      <Dialog open={showStaffDialog} onClose={() => setShowStaffDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Staff Member</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="User ID"
                value={staffForm.user}
                onChange={(e) => setStaffForm({ ...staffForm, user: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={staffForm.role}
                  onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })}
                  label="Role"
                >
                  <MenuItem value="receptionist">Receptionist</MenuItem>
                  <MenuItem value="nurse">Nurse</MenuItem>
                  <MenuItem value="admin">Administrator</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStaffDialog(false)}>Cancel</Button>
          <Button onClick={handleStaffAdd} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Add Staff'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onClose={() => setShowSettingsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Clinic Settings</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Clinic Name"
                value={settingsForm.clinic_name}
                onChange={(e) => setSettingsForm({ ...settingsForm, clinic_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={settingsForm.phone_number}
                onChange={(e) => setSettingsForm({ ...settingsForm, phone_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={settingsForm.address}
                onChange={(e) => setSettingsForm({ ...settingsForm, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={settingsForm.email}
                onChange={(e) => setSettingsForm({ ...settingsForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={settingsForm.city}
                onChange={(e) => setSettingsForm({ ...settingsForm, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Specialization"
                value={settingsForm.specialization}
                onChange={(e) => setSettingsForm({ ...settingsForm, specialization: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Subscription Plan</InputLabel>
                <Select
                  value={settingsForm.subscription_plan}
                  onChange={(e) => setSettingsForm({ ...settingsForm, subscription_plan: e.target.value })}
                  label="Subscription Plan"
                >
                  <MenuItem value="basic">Basic</MenuItem>
                  <MenuItem value="premium">Premium</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSettingsDialog(false)}>Cancel</Button>
          <Button onClick={handleSettingsUpdate} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Update Settings'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={showAnalyticsDialog} onClose={() => setShowAnalyticsDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Analytics Dashboard</DialogTitle>
        <DialogContent>
          {analyticsData && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Appointments"
                  value={analyticsData.total_appointments}
                  icon={<ScheduleIcon />}
                  color="primary.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Doctors"
                  value={analyticsData.total_doctors}
                  icon={<PersonIcon />}
                  color="secondary.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Staff"
                  value={analyticsData.total_staff}
                  icon={<PeopleIcon />}
                  color="success.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Monthly Revenue"
                  value={`$${analyticsData.monthly_revenue}`}
                  icon={<MonetizationOnIcon />}
                  color="info.main"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAnalyticsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClinicManagement;


















